'use client'
/* eslint-disable no-console */

import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { logService, LogEntry, LogLevel } from '@/services/logService'
import { appSettings } from '@/shared/consts/appSettings'
import { isWebView } from '@/utils/helpers/webViewHelper'

const isEnabledByDefault = () => {
  if (typeof window === 'undefined') return false

  try {
    if (window.location.search.includes('debugLogs=1')) return true
    return window.localStorage.getItem('DEBUG_LOGS') === '1'
  } catch {
    return false
  }
}

const isNonProductionWebView = () =>
  appSettings.environment !== 'production' && isWebView()

const levelColor: Record<LogLevel, string> = {
  debug: '#9ca3af',
  info: '#60a5fa',
  warn: '#fbbf24',
  error: '#f87171'
}

const formatTime = (ts: number) =>
  new Date(ts).toLocaleTimeString('pt-BR', { hour12: false })

export const DebugLogsOverlay = () => {
  const [searchParams] = useSearchParams()
  const [enabled, setEnabled] = useState(false)
  const [open, setOpen] = useState(false)
  const [levels, setLevels] = useState<Record<LogLevel, boolean>>({
    debug: true,
    info: true,
    warn: true,
    error: true
  })
  const [items, setItems] = useState<LogEntry[]>([])

  useEffect(() => {
    const qp = searchParams?.get('debugLogs')
    if (qp === '1') {
      try {
        window.localStorage.setItem('DEBUG_LOGS', '1')
      } catch {
        // ignore
      }
      setEnabled(true)
      return
    }

    setEnabled(isEnabledByDefault() || isNonProductionWebView())
  }, [searchParams])

  useEffect(() => {
    if (!enabled) return
    setItems(logService.getSnapshot())
    return logService.subscribe((entry) => setItems((prev) => [...prev, entry]))
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    if (typeof window === 'undefined') return

    const w = window as unknown as {
      __debugLogsOverlayConsoleHookInstalled?: boolean
      __debugLogsOverlayOriginalConsole?: Partial<Console>
    }

    if (w.__debugLogsOverlayConsoleHookInstalled) return
    w.__debugLogsOverlayConsoleHookInstalled = true

    w.__debugLogsOverlayOriginalConsole ??= {
      log: console.log,
      debug: console.debug,
      info: console.info,
      warn: console.warn,
      error: console.error
    }

    const original = w.__debugLogsOverlayOriginalConsole

    const toText = (args: unknown[]) => {
      try {
        return args
          .map((a) => (typeof a === 'string' ? a : JSON.stringify(a)))
          .join(' ')
      } catch {
        return '[unserializable console args]'
      }
    }

    const wrap =
      (level: LogLevel, method: keyof Console) =>
      (...args: unknown[]) => {
        logService.push(level, toText(args), args, 'console')
        const orig = original?.[method] as
          | ((...a: unknown[]) => void)
          | undefined
        orig?.(...args)
      }

    console.log = wrap('info', 'log')
    console.debug = wrap('debug', 'debug')
    console.info = wrap('info', 'info')
    console.warn = wrap('warn', 'warn')
    console.error = wrap('error', 'error')

    const onError = (event: ErrorEvent) => {
      logService.push(
        'error',
        event.message || 'window.onerror',
        { filename: event.filename, lineno: event.lineno, colno: event.colno },
        'window'
      )
    }

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      logService.push(
        'error',
        'unhandledrejection',
        { reason: event.reason },
        'window'
      )
    }

    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)

    logService.push(
      'info',
      'console hook instalado',
      undefined,
      'DebugLogsOverlay'
    )

    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)

      if (original?.log) console.log = original.log.bind(console)
      if (original?.debug) console.debug = original.debug.bind(console)
      if (original?.info) console.info = original.info.bind(console)
      if (original?.warn) console.warn = original.warn.bind(console)
      if (original?.error) console.error = original.error.bind(console)

      w.__debugLogsOverlayConsoleHookInstalled = false
    }
  }, [enabled])

  const filtered = useMemo(
    () => items.filter((i) => levels[i.level]),
    [items, levels]
  )

  if (!enabled) return null

  return (
    <div
      style={{ position: 'fixed', bottom: 12, right: 12, zIndex: 2147483647 }}
    >
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => {
            logService.clear()
            setItems([])
          }}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(17,24,39,0.9)',
            color: '#e5e7eb',
            fontSize: 12
          }}
        >
          Limpar
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.18)',
            background: open ? 'rgba(59,130,246,0.95)' : 'rgba(17,24,39,0.9)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600
          }}
        >
          Logs ({filtered.length})
        </button>
      </div>

      {open ? (
        <div
          style={{
            marginTop: 8,
            width: 'min(92vw, 720px)',
            height: 'min(60vh, 420px)',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(17,24,39,0.94)',
            color: '#e5e7eb',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)'
          }}
        >
          <div
            style={{
              padding: 10,
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.10)'
            }}
          >
            {(['debug', 'info', 'warn', 'error'] as const).map((lvl) => (
              <label
                key={lvl}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: levelColor[lvl]
                }}
              >
                <input
                  type="checkbox"
                  checked={levels[lvl]}
                  onChange={(e) =>
                    setLevels((prev) => ({ ...prev, [lvl]: e.target.checked }))
                  }
                />
                {lvl}
              </label>
            ))}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => {
                try {
                  window.localStorage.removeItem('DEBUG_LOGS')
                } catch {
                  // ignore
                }
                setEnabled(false)
                setOpen(false)
              }}
              style={{
                padding: '6px 8px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'transparent',
                color: '#e5e7eb',
                fontSize: 12
              }}
            >
              Desativar
            </button>
          </div>

          <div
            style={{
              padding: 10,
              height: 'calc(100% - 44px)',
              overflow: 'auto'
            }}
          >
            {filtered.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                Nenhum log ainda. Abra com `?debugLogs=1` e execute o fluxo.
              </div>
            ) : (
              filtered.map((e) => (
                <div
                  key={e.id}
                  style={{
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: 12,
                    lineHeight: 1.35,
                    padding: '8px 8px',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.08)',
                    marginBottom: 8,
                    background: 'rgba(0,0,0,0.18)'
                  }}
                >
                  <div
                    style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}
                  >
                    <span style={{ color: '#9ca3af' }}>{formatTime(e.at)}</span>
                    <span
                      style={{ color: levelColor[e.level], fontWeight: 700 }}
                    >
                      {e.level.toUpperCase()}
                    </span>
                    {e.scope ? (
                      <span style={{ color: '#c7d2fe' }}>[{e.scope}]</span>
                    ) : null}
                  </div>
                  <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
                    {e.message}
                  </div>
                  {typeof e.data !== 'undefined' ? (
                    <pre
                      style={{
                        marginTop: 6,
                        padding: 8,
                        borderRadius: 10,
                        background: 'rgba(0,0,0,0.25)',
                        overflow: 'auto',
                        color: '#e5e7eb'
                      }}
                    >
                      {JSON.stringify(e.data, null, 2)}
                    </pre>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
